/**
 * Form rules, wire parsing, and the employer's form library — backed by
 * chat-kit through the BFF's /api/forms lane (AI-2877). Rules and shapes
 * ported from the stream-chat-emp mock's `domain/form`; keep the names
 * diffable against it.
 */
import { useCallback, useEffect, useState, useSyncExternalStore } from 'react';
import {
  TEMPLATE_CATEGORIES,
  type FormDto,
  type FormLanguage,
  type TemplateCategory,
} from '@chat-emp/shared';

import type {
  AnsweredForm,
  Form,
  FormAnswer,
  FormField,
  FormId,
  FormNotice,
  FormResponse,
  Message,
} from '../fixtures/domain.js';
import {
  deleteForm as deleteFormApi,
  listForms as listFormsApi,
  listFormTemplates as listFormTemplatesApi,
  saveForm as saveFormApi,
} from '../lib/facade.js';
import { plural } from '../util/format.js';

// ── rules ────────────────────────────────────────────────────────────────────

/** The DEFAULT question cap: the session serves the live value
 *  (`Limits.formMaxFields`, FORM_MAX_FIELDS on the server), and this is what
 *  stands in until it has. */
export const MAX_FIELDS = 20;

/** The longest written answer, as chat-kit stores it. */
export const MAX_ANSWER = 500;

export { MAX_DESCRIPTION } from '@chat-emp/shared';

/** Two options is the least that is a question. */
export const MIN_OPTIONS = 2;

export const MAX_OPTIONS = 4;

/** A hundred, not eighty: the Malay wording of six catalogue questions already
 *  passes eighty, and a question that fits the document has to fit the editor. */
export const MAX_LABEL = 100;
export const MAX_OPTION_LABEL = 40;
export const MAX_FORM_NAME = 50;

export function canAddField(fields: FormField[], max: number = MAX_FIELDS): boolean {
  return fields.length < max;
}

export function canAddOption(options: unknown[]): boolean {
  return options.length < MAX_OPTIONS;
}

export function fieldComplete(field: FormField): boolean {
  if (field.label.trim() === '') return false;
  if (field.type === 'text') return true;
  return (
    field.options.length >= MIN_OPTIONS &&
    field.options.every((option) => option.label.trim() !== '')
  );
}

/** What Save writes. The builder keeps a text question's options while it is
 *  one, so switching back costs nothing typed; the saved form carries none. */
export function settled(form: Form): Form {
  return {
    ...form,
    fields: form.fields.map(({ description, ...field }) => ({
      ...field,
      ...(description?.trim() ? { description: description.trim() } : {}),
      options: field.type === 'text' ? [] : field.options,
    })),
  };
}

/** Whether this form may be saved. There are no drafts: the library holds only
 *  sendable forms, so no surface downstream re-checks this. */
export function isSendable(form: Form): boolean {
  return form.name.trim() !== '' && form.fields.length > 0 && form.fields.every(fieldComplete);
}

/** Whether two drafts say the same thing, field by field, option by option —
 *  what "dirty" is in the builder. Ids are ignored: a template copy mints
 *  fresh ones, and identical words are still nothing to lose. */
export function sameForm(left: Form, right: Form): boolean {
  const a = settled(left);
  const b = settled(right);
  return (
    a.name === b.name &&
    a.fields.length === b.fields.length &&
    a.fields.every((field, index) => {
      const other = b.fields[index];
      return (
        other !== undefined &&
        field.label === other.label &&
        field.description === other.description &&
        field.type === other.type &&
        field.options.length === other.options.length &&
        field.options.every((option, spot) => option.label === other.options[spot]?.label)
      );
    })
  );
}

/**
 * A form from a template, unsaved: the twin of `blankForm`. Every id is minted
 * fresh so the copy can drift and a template rewritten later can never reach
 * into a form somebody has been sending for months. Save is still the only
 * write — chat-kit's /forms/copy is deliberately unused, since it writes a
 * library row before the employer has read what they are about to own.
 */
export function formFromTemplate(template: Form): Form {
  return {
    id: crypto.randomUUID(),
    ...(template.language ? { language: template.language } : {}),
    name: template.name,
    fields: template.fields.map((field) => ({
      ...field,
      id: crypto.randomUUID(),
      options: field.options.map((option) => ({ ...option, id: crypto.randomUUID() })),
    })),
    updatedAt: new Date().toISOString(),
  };
}

/** The library entry, frozen for sending. Deep-copied so an edit next week
 *  cannot rewrite questions somebody answered last week. */
export function snapshotOf(form: Form): FormNotice {
  return { formId: form.id, name: form.name, fields: structuredClone(form.fields) };
}

/** The notice on a message, or nothing — a notice with no questions is not one. */
export function formOf(message: Message): FormNotice | null {
  const form = message.form;
  return form && form.fields.length > 0 ? form : null;
}

export function isAnswered(notice: FormNotice): boolean {
  return notice.answeredAt !== undefined;
}

/**
 * Every form answered in a thread, each response paired with the questions it
 * was given to. The thread is the store; a response whose asking is not in the
 * loaded window is left out rather than rendered as bare option ids.
 */
export function answeredFormsIn(messages: Message[]): AnsweredForm[] {
  const byId = new Map(messages.map((message) => [message.id, message]));
  return messages.flatMap((message) => {
    const response = message.formResponse;
    if (!response) return [];
    const notice = byId.get(response.noticeMessageId)?.form;
    return notice ? [{ response, notice }] : [];
  });
}

/** What came back for one question: the written words, or the picked labels. */
export function answerValues(field: FormField, answers: FormAnswer[]): string[] {
  const text = answers.find((entry) => entry.fieldId === field.id)?.text?.trim();
  return text ? [text] : answerLabels(field, answers);
}

/** A field's answer as labels. An id matching no option is dropped — a stray
 *  uuid mid-sentence is the worst way to learn a snapshot failed. */
export function answerLabels(field: FormField, answers: FormAnswer[]): string[] {
  const answer = answers.find((entry) => entry.fieldId === field.id);
  if (!answer) return [];
  return answer.optionIds
    .map((id) => field.options.find((option) => option.id === id)?.label)
    .filter((label): label is string => label !== undefined);
}

// ── wire ─────────────────────────────────────────────────────────────────────
// The notice and response ride Stream messages as top-level `form` /
// `form_response` custom fields: snake_case keys, but the field `type` stays
// the domain's own camelCase `multiChoice` — chat-kit's dispatch notice and
// chat-js's mapper agree on that, and this parser matches them.

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function toField(source: unknown): FormField | null {
  if (!isRecord(source)) return null;
  const { id, label, type } = source;
  if (typeof id !== 'string' || id === '' || typeof label !== 'string') return null;
  if (type !== 'choice' && type !== 'multiChoice' && type !== 'text') return null;
  const description =
    typeof source.description === 'string' && source.description.trim() !== ''
      ? { description: source.description }
      : {};
  if (type === 'text') return { id, label, ...description, type, options: [] };
  const options = Array.isArray(source.options)
    ? source.options.flatMap((option) =>
        isRecord(option) && typeof option.id === 'string' && typeof option.label === 'string'
          ? [{ id: option.id, label: option.label }]
          : [],
      )
    : [];
  if (options.length === 0) return null;
  return { id, label, ...description, type, options };
}

/** Parse a wire `form` field. Nothing upstream validates it, so a malformed
 *  payload degrades to no card rather than throwing mid-thread. */
export function toFormNotice(source: unknown): FormNotice | undefined {
  if (!isRecord(source)) return undefined;
  const { form_id: formId, name } = source;
  if (typeof formId !== 'string' || typeof name !== 'string') return undefined;
  const fields = Array.isArray(source.fields)
    ? source.fields.flatMap((field) => toField(field) ?? [])
    : [];
  if (fields.length === 0) return undefined;
  return {
    formId,
    name,
    fields,
    ...(typeof source.answered_at === 'string' ? { answeredAt: source.answered_at } : {}),
    ...(typeof source.response_message_id === 'string'
      ? { responseMessageId: source.response_message_id }
      : {}),
  };
}

/** Parse a wire `form_response` field, same posture as `toFormNotice`. */
export function toFormResponse(source: unknown): FormResponse | undefined {
  if (!isRecord(source)) return undefined;
  const {
    id,
    form_id: formId,
    notice_message_id: noticeMessageId,
    submitted_at: submittedAt,
  } = source;
  if (
    typeof id !== 'string' ||
    typeof formId !== 'string' ||
    typeof noticeMessageId !== 'string' ||
    typeof submittedAt !== 'string'
  ) {
    return undefined;
  }
  const answers = Array.isArray(source.answers)
    ? source.answers.flatMap((answer): FormAnswer[] => {
        if (!isRecord(answer) || typeof answer.field_id !== 'string') return [];
        const optionIds = Array.isArray(answer.option_ids)
          ? answer.option_ids.filter((option): option is string => typeof option === 'string')
          : [];
        return [
          {
            fieldId: answer.field_id,
            optionIds,
            ...(typeof answer.text === 'string' ? { text: answer.text } : {}),
          },
        ];
      })
    : [];
  if (answers.length === 0) return undefined;
  return { id, formId, noticeMessageId, answers, submittedAt };
}

// ── library ──────────────────────────────────────────────────────────────────
// The library lives in chat-kit, reached through the BFF's /api/forms lane.
// Versioning stays server-side: an edit appends an immutable version there,
// and the client always reads the current one.

const toForm = (dto: FormDto): Form => ({
  id: dto.id,
  name: dto.name,
  fields: dto.fields,
  updatedAt: dto.updatedAt,
  ...(dto.language ? { language: dto.language } : {}),
  ...(dto.category ? { category: dto.category } : {}),
});

/** Short form for a meta line, full name for a title or a switch. */
export const LANGUAGE_LABEL: Record<FormLanguage, { short: string; name: string }> = {
  en: { short: 'EN', name: 'English' },
  ms: { short: 'BM', name: 'Bahasa Melayu' },
  zh: { short: '中文', name: 'Mandarin' },
};

// ── catalogue ───────────────────────────────────────────────────────────────
// The shared templates, read one language at a time. A template without a
// category is not the catalogue's (the three legacy rows) and stays out of
// every grouped surface.

export { TEMPLATE_CATEGORIES };

export const CATEGORY_LABEL: Record<TemplateCategory, { name: string; tagline: string }> = {
  general: { name: 'General screening', tagline: 'Suits any role' },
  non_executive: { name: 'Non-executive', tagline: 'Retail, F&B, logistics, operations' },
  executive: { name: 'Executive & professional', tagline: 'Sales, marketing, HR, finance, tech' },
};

export function templatesIn(
  templates: Form[],
  language: FormLanguage,
  category?: TemplateCategory,
): Form[] {
  return templates.filter(
    (template) =>
      template.category !== undefined &&
      template.language === language &&
      (category === undefined || template.category === category),
  );
}

/** Name and question text, not options: a query for "weekend" should find the
 *  shift templates, not every form whose answers include one. */
export function matchesTemplate(template: Form, query: string): boolean {
  const needle = query.trim().toLowerCase();
  if (needle === '') return true;
  return (
    template.name.toLowerCase().includes(needle) ||
    template.fields.some((field) => field.label.toLowerCase().includes(needle))
  );
}

/** "4 questions · BM": the language rides with the count wherever a form is
 *  listed, and only when the form has one, so older library rows say nothing new. */
export function formMeta(form: Pick<Form, 'fields' | 'language'>): string {
  const count = plural(form.fields.length, 'question');
  return form.language ? `${count} · ${LANGUAGE_LABEL[form.language].short}` : count;
}

/** chat-kit ids are 32-hex; the editor's draft ids are not, which is how a
 *  save knows create from edit. */
const KIT_ID = /^[0-9a-f]{32}$/;

let revision = 0;
const listeners = new Set<() => void>();

function changed(): void {
  revision += 1;
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export interface UseForms {
  /** The employer's own library, newest edit first. */
  forms: Form[];
  /** The shared catalogue: dispatchable by everyone, editable by nobody. */
  templates: Form[];
  loading: boolean;
  /** The read failed. `reload` asks again. */
  error: boolean;
  saving: boolean;
  save: (form: Form) => Promise<Form>;
  remove: (id: FormId) => Promise<void>;
  reload: () => void;
}

/**
 * The library, live: one revision counter behind every reader, so the panel,
 * the picker and the editor cannot disagree about what the library holds.
 */
export function useForms(): UseForms {
  const [forms, setForms] = useState<Form[]>([]);
  const [templates, setTemplates] = useState<Form[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [saving, setSaving] = useState(false);
  const tick = useSyncExternalStore(subscribe, () => revision);

  useEffect(() => {
    let active = true;
    setError(false);
    void Promise.all([listFormsApi(), listFormTemplatesApi()])
      .then(([own, shared]) => {
        if (!active) return;
        setForms(own.map(toForm));
        setTemplates(shared.map(toForm));
        setLoading(false);
      })
      .catch(() => {
        if (!active) return;
        setError(true);
        setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [tick]);

  const write = useCallback(async <T>(operation: () => Promise<T>): Promise<T> => {
    setSaving(true);
    try {
      const result = await operation();
      changed();
      return result;
    } finally {
      setSaving(false);
    }
  }, []);

  const save = useCallback(
    (form: Form) =>
      write(async () => {
        const dto = await saveFormApi({
          ...(KIT_ID.test(form.id) ? { formId: form.id } : {}),
          name: form.name,
          fields: form.fields,
          ...(form.language ? { language: form.language } : {}),
        });
        const saved = toForm(dto);
        // Seated before the refetch below lands, newest first. A caller that
        // opens what it just saved would otherwise read a library that does not
        // hold it yet and show its "nothing selected" state for a round trip.
        setForms((current) => [saved, ...current.filter((entry) => entry.id !== saved.id)]);
        return saved;
      }),
    [write],
  );
  const remove = useCallback((id: FormId) => write(() => deleteFormApi(id)), [write]);

  const reload = useCallback(() => {
    setLoading(true);
    setError(false);
    changed();
  }, []);

  return { forms, templates, loading, error, saving, save, remove, reload };
}
